import {
  GetInstallationPlansDocument,
  GetInstallationPlansQuery,
  SaveInstallation_Input,
  SaveInstallationMutation,
  useSaveInstallationMutation,
  useSaveInstallationPlanFormQuery
} from 'generated/graphql';
import React, { useEffect } from 'react';


import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';


export type IFormState = {
  id: string;
  planId: string;
  projectId: string;
};

const initialState: IFormState = {
  id: '',
  planId: '',
  projectId: ''
};

export function useFormState(id: string, projectId: string) {
  const {
    formState,
    dispatch,
    genFieldProps
  } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      id: {
        label: 'Installation ID',
        type: 'text'
      },
      planId: {
        label: 'Plan',
        type: 'text',
        required: true
      },
      projectId: {
        label: 'projectId',
        type: 'text'
      }
    }
  });

  useEffect(() => {
    if (projectId !== '' && formState.projectId === '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'projectId',
          value: projectId
        }
      });
    }
  }, [
    dispatch,
    formState.projectId,
    projectId
  ]);

  const { data, loading, called } = useQuery(id);

  useEffect(() => {
    if (data.id !== '' && formState.id === '' && !loading && called) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
  }, [
    called,
    data,
    dispatch,
    formState,
    loading
  ]);

  return React.useMemo(
    () => {


      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}



export function useSaveInstall() {
  const [
    fnMutate,
    tuple
  ] = useSaveInstallationMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (input: SaveInstallation_Input) => {
      await fnMutate({
        variables: {
          input: {
            ...input
          }
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<GetInstallationPlansQuery, SaveInstallationMutation>(GetInstallationPlansDocument, (prevState, { mutationResult }) => {
            if (!prevState.installation) {
              return prevState;
            }
            if (!mutationResult.data?.saveInstallation?.plan) {
              return prevState;
            }
            return {
              ...prevState,
              installation: {
                ...prevState.installation,
                plan: {
                  ...prevState.installation.plan,
                  ...mutationResult.data.saveInstallation.plan
                }
              }
            };
            return prevState;
          })
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}


export interface IData extends Omit<IFormState, 'projectId'> {}

export function useQuery(id: string) {
  const tuple = useSaveInstallationPlanFormQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {
        id: tuple.data?.installation?.id ?? '',
        planId: tuple.data?.installation?.plan?.id ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
